import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import TerritoryUnitSelect from "../../components/common/TerritoryUnitSelect";
import { NO_TERRITORY } from "../../components/common/TerritoryLabel";
import { Loader2, MapPin, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";

/**
 * Bulk territory resolution for flagged doctors (#26).
 *
 * The migration flags 94 doctors on "Patna" alone — four zones, unguessable.
 * Resolving those one dialog at a time is not work anybody actually does, so
 * the flag would simply persist forever. This makes the migration finishable,
 * which is why it ships with it rather than after it.
 *
 * Selection lives here rather than in the page: it is only ever used by this
 * flow, and hoisting it would put bulk state on every doctor list render.
 */
export default function BulkTerritoryBar({ doctors, canBulkEdit, onDone }) {
  const [selected, setSelected] = useState(() => new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [unit, setUnit] = useState("");
  const [busy, setBusy] = useState(false);

  const flagged = doctors.filter((d) => d.territory_unmigrated);
  if (!canBulkEdit || flagged.length === 0) return null;

  const allSelected = selected.size > 0 && selected.size === flagged.length;

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function apply() {
    setBusy(true);
    try {
      const { data } = await api.put("/doctors/bulk/territory", {
        doctor_ids: [...selected],
        territory_unit: unit,
      });
      if (isPendingMasterChange(data)) {
        toast.success(sentForApprovalMessage(data));
      } else {
        toast.success(`${data.updated} doctor${data.updated === 1 ? "" : "s"} set to ${data.territory_unit}`);
        if (data.not_found) {
          toast.error(`${data.not_found} could not be found — the list may be stale.`);
        }
      }
      setSelected(new Set());
      setUnit("");
      setDialogOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  // Group by the unresolved value, because that is how the decision is made:
  // "all 94 'Patna' doctors go to Patna East" is one judgement, not 94.
  //
  // RC-10: the no-value group was keyed "—" and then rendered inside literal
  // quotes as a heading — `"—"` read as a stored territory called "—". The
  // missing case uses the one shared string and drops the quotes with it.
  const byValue = {};
  for (const d of flagged) {
    const key = d.area || d.city || NO_TERRITORY;
    (byValue[key] = byValue[key] || []).push(d);
  }

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-xl p-3 md:p-4 space-y-3"
      data-testid="bulk-territory-bar"
    >
      <div className="flex items-start gap-2">
        <TriangleAlert size={16} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-amber-900">
            {flagged.length} doctor{flagged.length === 1 ? "" : "s"} need a territory unit
          </p>
          <p className="text-[11px] text-amber-800 mt-0.5">
            The migration would not guess these. Select a group and assign the unit in one action.
          </p>
        </div>
        <Button
          size="sm"
          disabled={selected.size === 0}
          onClick={() => setDialogOpen(true)}
          className="h-8 bg-callisto-navy hover:bg-callisto-navy/90 text-white text-[11px] shrink-0"
          data-testid="bulk-territory-open"
        >
          <MapPin size={12} className="mr-1" />
          Assign unit{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
      </div>

      <label className="flex items-center gap-2 text-[11px] text-amber-900 cursor-pointer">
        <Checkbox
          checked={allSelected}
          onCheckedChange={() =>
            setSelected(allSelected ? new Set() : new Set(flagged.map((d) => d.id)))
          }
          aria-label="Select all flagged doctors"
          data-testid="bulk-territory-select-all"
        />
        Select all {flagged.length}
      </label>

      <div className="space-y-2">
        {Object.entries(byValue).map(([value, group]) => {
          const ids = group.map((d) => d.id);
          const groupSelected = ids.every((id) => selected.has(id));
          return (
            <div key={value} className="rounded-lg bg-white border border-amber-200 p-2.5" data-testid={`bulk-group-${value}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={groupSelected}
                  onCheckedChange={() =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      ids.forEach((id) => (groupSelected ? next.delete(id) : next.add(id)));
                      return next;
                    })
                  }
                  aria-label={`Select all ${value}`}
                  data-testid={`bulk-group-select-${value}`}
                />
                <span className="text-xs font-bold text-callisto-navy">
                  {value === NO_TERRITORY ? NO_TERRITORY : `"${value}"`}
                </span>
                <span className="text-[11px] text-callisto-muted">
                  {group.length} doctor{group.length === 1 ? "" : "s"}
                </span>
              </label>
              <div className="mt-1.5 pl-6 flex flex-wrap gap-x-3 gap-y-1">
                {group.slice(0, 12).map((d) => (
                  <label key={d.id} className="flex items-center gap-1.5 text-[11px] text-callisto-body cursor-pointer">
                    <Checkbox
                      checked={selected.has(d.id)}
                      onCheckedChange={() => toggle(d.id)}
                      aria-label={`Select ${d.name}`}
                      data-testid={`bulk-select-${d.id}`}
                    />
                    {d.name}
                  </label>
                ))}
                {group.length > 12 && (
                  <span className="text-[11px] text-callisto-muted">
                    +{group.length - 12} more (covered by the group checkbox)
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent data-testid="bulk-territory-dialog">
          <DialogHeader>
            <DialogTitle>
              Assign a unit to {selected.size} doctor{selected.size === 1 ? "" : "s"}
            </DialogTitle>
            <DialogDescription>
              Every selected doctor gets this unit and their review flag clears.
              This is not reversible in bulk — re-selecting and reassigning is.
            </DialogDescription>
          </DialogHeader>
          <TerritoryUnitSelect value={unit} onChange={setUnit} testId="bulk-territory-unit" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={apply}
              disabled={busy || !unit}
              className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="bulk-territory-apply"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
